import { fetchAuthSession, signInWithRedirect, signOut } from 'aws-amplify/auth';
import { isNil, negate, uniq } from 'lodash-es';
import { inject, singleton } from 'tsyringe';
import {
  CognitoIdentityProviderClient,
  GetUserCommand,
  NotAuthorizedException,
  UserNotFoundException,
} from '@aws-sdk/client-cognito-identity-provider';
import { RoutePaths } from '../../../config/route-paths';
import { Optional } from '../../../lib/types/Optional';
import { UserPoolUtils } from '../../utils/user-pool/UserPoolUtils';
import { ApiCacheService } from '../communication/api-cache/ApiCacheService';
import { CognitoError } from './errors/CognitoError';
import { IPermission, IUserInfo } from './interfaces/Authentication.types';
import {
  IAccessTokenPayload,
  IAuthenticationTokens,
  IIdTokenPayload,
  OidcTokenType,
} from './interfaces/IAuthenticationTokens';
import { PermissionsByRole } from 'config/permissions';

const CognitoIpClient = new CognitoIdentityProviderClient({ region: process.env.REACT_APP_AWS_REGION });

@singleton()
export class AuthenticationService {
  constructor(
    @inject('UserPoolProviderName') private providerName: string,
    // Typescript is reporting the error "Attempted import error: 'BrowserHistory' is not exported from 'redux'"
    // Set to any type as a workaround
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    @inject('History') private history: any,
    private userPoolUtils: UserPoolUtils,
    private apiCacheService: ApiCacheService
  ) {}

  public signIn = async (): Promise<void> => {
    try {
      await signInWithRedirect({
        provider: { custom: this.providerName },
        options: { prompt: 'LOGIN' },
      });
    } catch (error) {
      console.error(error);
    }
  };

  public triggerTokenRefresh = async (): Promise<void> => {
    try {
      await fetchAuthSession({ forceRefresh: true });
    } catch (error) {
      if (error === CognitoError.REFRESH_TOKEN_EXPIRED) {
        await this.signOut();
        this.history.push(RoutePaths.LOGIN);
        return;
      }
      // No refresh needed when not logged in
      if (error !== CognitoError.NO_CURRENT_USER) {
        throw error;
      }
    }
  };

  public checkIsAccessTokenRevoked = async (): Promise<void> => {
    try {
      const { tokens } = await fetchAuthSession();

      if (!tokens?.accessToken) {
        return;
      }

      const command = new GetUserCommand({
        AccessToken: tokens.accessToken.toString(),
      });
      await CognitoIpClient.send(command);
    } catch (error) {
      if (
        (error instanceof NotAuthorizedException && error.message === CognitoError.ACCESS_TOKEN_REVOKED) ||
        (error instanceof UserNotFoundException && error.message === CognitoError.USER_DOES_NOT_EXIST)
      ) {
        await this.signOut();
        this.history.push(RoutePaths.LOGIN);
        return;
      }
      throw error;
    }
  };

  public signOut = async (): Promise<void> => {
    this.clearClientSession();
    await signOut({ global: true });
  };

  public signOutAndRedirect = async (): Promise<void> => {
    // Clear in-memory cache only. Do not clear localStorage here — Amplify needs the
    // OAuth sign-in flag to redirect to Cognito logout. Storage is cleared in LogoutCallback.
    this.apiCacheService.clear();
    const logoutCallbackUrl = `${window.location.origin}${RoutePaths.LOGOUT}`;

    await signOut({
      global: true,
      oauth: {
        redirectUrl: logoutCallbackUrl,
      },
    });
  };

  private clearClientSession = (): void => {
    this.apiCacheService.clear();
    localStorage.clear();
  };

  public refreshSession = async (): Promise<void> => {
    try {
      await fetchAuthSession({ forceRefresh: true });
    } catch (error: any) {
      console.error(error);
    }
  };

  public checkAuthentication = async (): Promise<Optional<IAuthenticationTokens>> => {
    const tokens = await this.getTokens();

    return tokens;
  };

  public getTokens = async (): Promise<Optional<IAuthenticationTokens>> => {
    let returnTokens: Optional<IAuthenticationTokens>;

    try {
      const { tokens } = await fetchAuthSession();

      returnTokens = {
        jwt: {
          idToken: tokens?.idToken?.toString(),
          accessToken: tokens?.accessToken?.toString(),
          refreshToken: '', // refreshToken is not directly exposed in v6 session
        },
        decoded: {
          idToken: tokens?.idToken?.payload as unknown as IIdTokenPayload,
          accessToken: tokens?.accessToken?.payload as unknown as IAccessTokenPayload,
        },
      };
    } catch (error) {
      if (error === CognitoError.NO_CURRENT_USER) {
        returnTokens = null;
      } else {
        console.error(error);
      }
    }

    if (returnTokens !== null && returnTokens?.jwt.idToken && returnTokens?.jwt.accessToken) {
      try {
        this.userPoolUtils.verifyOidcToken(returnTokens?.jwt.idToken, OidcTokenType.ID);
        this.userPoolUtils.verifyOidcToken(returnTokens?.jwt.accessToken, OidcTokenType.ACCESS);
      } catch (error) {
        console.error(error);
        await this.signOut();
        this.history.push(RoutePaths.LOGIN);
      }
    }

    return returnTokens;
  };

  public getPermissions = async (): Promise<IPermission[]> => {
    const tokens = await this.getTokens();
    const roles = tokens?.decoded.accessToken?.['cognito:groups'] ?? [];
    // eslint-disable-next-line prefer-const
    let permissions = roles.flatMap(role => PermissionsByRole[role]).filter(negate(isNil));

    // Comment / uncomment permissions for testing

    // permissions = [
    //   Permission.Customer.Account.UPDATE,
    //   Permission.Customer.Role.UPDATE,
    //   Permission.Customer.User.AUTHORIZE,
    //   Permission.Customer.User.UNAUTHORIZE,
    //   Permission.Customer.User.SEND_INVITATION,
    //   Permission.Customer.User.DELETE,
    //   Permission.Internal.Role.UPDATE,
    //   Permission.Machine.CLAIM,
    //   Permission.Machine.UPDATE,
    //   Permission.Machine.ActivationStatus.UPDATE,
    //   Permission.Machine.Comment.CREATE,
    //   Permission.Site.CREATE,
    //   Permission.Site.READ,
    //   Permission.Site.UPDATE,
    //   Permission.Site.DELETE,
    //   Permission.Site.Machine.UPDATE,
    //   Permission.Site.StrategicManager.UPDATE,
    //   Permission.Site.WorkInterval.UPDATE,
    //   Permission.Permissions.READ,
    // ];

    return uniq(permissions);
  };

  public getUserInfo = async (): Promise<IUserInfo> => {
    try {
      const { tokens } = await fetchAuthSession();
      const email = tokens?.idToken?.payload?.email as string;

      return {
        email,
      };
    } catch (_error) {
      return {
        email: undefined,
      };
    }
  };

  public getUserGroups = async (): Promise<string[]> => {
    try {
      const { tokens } = await fetchAuthSession();
      const groups: string[] = (tokens?.idToken?.payload?.['cognito:groups'] as string[]) || [];

      return groups;
    } catch (_error) {
      console.error('Error reading user groups');
      return [];
    }
  };
}
